// Banking — the bank and card accounts as the ledger sees them: import a
// statement, sort each line into an account, match what is already posted,
// and reconcile against the statement balance.
import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useBooksAccess } from '../../contexts/AuthContext';
import { ConfirmButton, Empty, ErrorNote, Field, Note, Section } from '../../components/ui';
import { day, dollars } from '../../lib/format';
import { FAR_PAST, bank, journal, reports, vendors as vendorsApi } from '../../lib/ledger/api';
import { SOURCE_LABELS } from '../../lib/ledger/types';
import type { Account, BankAccount, BankImportResult, BankReconciliation, BankTransaction, JournalEntry, Vendor } from '../../lib/ledger/types';
import { addDays, balanceOf } from '../../lib/ledger/reports';
import { COLUMN_LABELS, detectColumns, parseCsv, toImportRows } from '../../lib/ledger/csv';
import type { ColumnMap } from '../../lib/ledger/csv';
import { AccountSelect, Money, MoneyInput, ReadOnlyNote, downloadCsv, today, useAccounts } from './shared';

function useLoad<T>(fn: () => Promise<T>, deps: readonly unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => { if (alive) setData(d); })
      .catch((e: unknown) => { if (alive) setError(e); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, error, loading, reload: () => setTick((t) => t + 1) };
}

const KIND_WORDS: Record<BankAccount['kind'], string> = {
  bank: 'Bank account',
  vessel: 'Operating cash',
  credit_card: 'Credit card',
  other: 'Other',
};

const STATUS_WORDS: Record<BankTransaction['status'], string> = {
  for_review: 'For review',
  posted: 'Posted',
  excluded: 'Excluded',
};

/** Accounts a bank or card row may point at: cash-like assets and credit cards. */
function glCandidates(accounts: Account[]): Account[] {
  return accounts.filter((a) => (a.type === 'asset' && (a.subtype === 'bank' || a.subtype === 'vessel')) || (a.type === 'liability' && a.subtype === 'credit_card'));
}

export function BankingTab() {
  const { canEdit } = useBooksAccess();
  const chart = useAccounts();
  const list = useLoad(() => bank.accounts(), []);
  const tb = useLoad(() => reports.trialBalance(FAR_PAST, today()), []);
  const vendorList = useLoad(() => vendorsApi.list(), []);
  const [selected, setSelected] = useState('');

  const rows = list.data ?? [];
  useEffect(() => {
    if (!selected && rows.length) setSelected((rows.find((a) => a.is_active) ?? rows[0]).id);
  }, [rows, selected]);

  const balances = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of tb.data ?? []) m.set(r.account_id, balanceOf(r, { includeOpening: true }));
    return m;
  }, [tb.data]);

  const current = rows.find((a) => a.id === selected) ?? null;

  return (
    <>
      {!canEdit && <ReadOnlyNote />}
      <ErrorNote error={chart.error} />
      <AccountsSection
        rows={rows}
        loading={list.loading}
        error={list.error}
        accounts={chart.accounts}
        byId={chart.byId}
        balances={balances}
        balancesLoading={tb.loading}
        canEdit={canEdit}
        selected={selected}
        onSelect={setSelected}
        onChanged={() => { list.reload(); tb.reload(); }}
      />
      <ImportSection rows={rows} selected={selected} canEdit={canEdit} onImported={() => tb.reload()} />
      {current && (
        <TransactionsSection
          key={current.id}
          account={current}
          accounts={chart.accounts}
          byId={chart.byId}
          vendors={vendorList.data ?? []}
          canEdit={canEdit}
          onPosted={() => tb.reload()}
        />
      )}
      {current && <ReconcileSection key={`r-${current.id}`} account={current} ledgerBalance={balances.get(current.account_id) ?? null} canEdit={canEdit} />}
    </>
  );
}

// ── accounts ─────────────────────────────────────────────────────────────────

function AccountsSection({ rows, loading, error, accounts, byId, balances, balancesLoading, canEdit, selected, onSelect, onChanged }: {
  rows: BankAccount[];
  loading: boolean;
  error: unknown;
  accounts: Account[];
  byId: Map<string, Account>;
  balances: Map<string, number>;
  balancesLoading: boolean;
  canEdit: boolean;
  selected: string;
  onSelect: (id: string) => void;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', kind: 'bank' as BankAccount['kind'], account_id: '', institution: '', last4: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  async function add() {
    setBusy(true); setErr(null); setDone(null);
    try {
      await bank.addAccount({ name: form.name.trim(), kind: form.kind, account_id: form.account_id, institution: form.institution.trim() || null, last4: form.last4.trim() || null });
      setDone(`Added ${form.name.trim()}.`);
      setForm({ name: '', kind: 'bank', account_id: '', institution: '', last4: '' });
      setAdding(false);
      onChanged();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  async function setActive(a: BankAccount, on: boolean) {
    setErr(null); setDone(null);
    try { await bank.setActive(a.id, on); setDone(`${a.name} is ${on ? 'active again' : 'no longer active'}.`); onChanged(); } catch (e) { setErr(e); }
  }

  const hasVessel = rows.some((a) => a.kind === 'vessel');
  const canAdd = form.name.trim() && form.account_id;

  return (
    <Section
      title="Accounts"
      right={<button type="button" className="lp-btn lp-btn--quiet lp-no-print" disabled={!canEdit} onClick={() => setAdding((v) => !v)}>{adding ? 'Close' : 'Add an account'}</button>}
    >
      <ErrorNote error={error} />
      <ErrorNote error={err} />
      {done && <Note tone="good">{done}</Note>}
      {loading ? <Empty>Loading accounts…</Empty> : rows.length === 0 ? <Empty>No bank or card accounts yet.</Empty> : (
        <div className="lp-tbl-wrap">
          <table className="lp-tbl">
            <thead>
              <tr><th>Account</th><th>Kind</th><th>Ledger account</th><th className="num">Ledger balance</th><th>Status</th><th className="lp-no-print" /></tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const gl = byId.get(a.account_id);
                const bal = balances.get(a.account_id);
                return (
                  <tr key={a.id} className={a.is_active ? undefined : 'quiet'}>
                    <td>
                      <div>{a.name}</div>
                      <div className="muted">{[a.institution, a.last4 ? `····${a.last4}` : null].filter(Boolean).join(' · ') || '—'}</div>
                    </td>
                    <td>{KIND_WORDS[a.kind]}</td>
                    <td>{gl ? `${gl.code} · ${gl.name}` : '—'}</td>
                    <td className="num">{balancesLoading ? <span className="muted">…</span> : <Money cents={bal ?? 0} />}</td>
                    <td>
                      <span className={`lp-tag ${a.is_active ? 'lp-tag--good' : ''}`}>{a.is_active ? 'Active' : 'Inactive'}</span>
                      {a.id === selected && <span className="lp-tag" style={{ marginLeft: 6 }}>Selected</span>}
                    </td>
                    <td className="lp-no-print">
                      <div className="lp-row">
                        {a.id !== selected && <button type="button" className="lp-btn lp-btn--small" onClick={() => onSelect(a.id)}>Work on this</button>}
                        {a.is_active
                          ? <ConfirmButton label="Deactivate" sentence={`Deactivate ${a.name}? Its history stays; it leaves the active list.`} yes="Yes, deactivate" disabled={!canEdit} onConfirm={() => setActive(a, false)} />
                          : <button type="button" className="lp-btn lp-btn--small" disabled={!canEdit} onClick={() => void setActive(a, true)}>Reactivate</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {adding && canEdit && (
        <div className="lp-form" style={{ marginTop: 12 }}>
          <div className="lp-form-grid">
            <Field label="Name"><input className="lp-inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rootegra checking" /></Field>
            <Field label="Kind">
              <select className="lp-inp" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as BankAccount['kind'] })}>
                {(Object.keys(KIND_WORDS) as BankAccount['kind'][]).map((k) => <option key={k} value={k}>{KIND_WORDS[k]}</option>)}
              </select>
            </Field>
            <Field label="Ledger account" hint="A cash account or a credit card in the chart.">
              <AccountSelect value={form.account_id} onChange={(id) => setForm({ ...form, account_id: id })} accounts={glCandidates(accounts)} activeOnly={false} />
            </Field>
            <Field label="Institution"><input className="lp-inp" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} placeholder="Mercury" /></Field>
            <Field label="Last 4"><input className="lp-inp" value={form.last4} maxLength={4} inputMode="numeric" onChange={(e) => setForm({ ...form, last4: e.target.value.replace(/\D/g, '') })} /></Field>
          </div>
          <div className="lp-row">
            <button type="button" className="lp-btn lp-btn--primary" disabled={busy || !canAdd} onClick={() => void add()}>{busy ? 'Adding…' : 'Add account'}</button>
            <button type="button" className="lp-btn lp-btn--quiet" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ── import ───────────────────────────────────────────────────────────────────

type Parsed = { filename: string; header: string[]; rows: string[][]; map: ColumnMap };
const MAP_FIELDS: (keyof ColumnMap)[] = ['date', 'amount', 'credit', 'debit', 'description', 'counterparty', 'id'];

function ImportSection({ rows, selected, canEdit, onImported }: { rows: BankAccount[]; selected: string; canEdit: boolean; onImported: () => void }) {
  const [accountId, setAccountId] = useState(selected);
  useEffect(() => { setAccountId(selected); }, [selected]);
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BankImportResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [fileKey, setFileKey] = useState(0);

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setResult(null); setError(null);
    try {
      const all = parseCsv(await f.text());
      if (all.length < 2) { setError(new Error('That file has no rows under its header line.')); return; }
      const header = all[0];
      setParsed({ filename: f.name, header, rows: all.slice(1), map: detectColumns(header) });
    } catch (err) { setError(err); }
  }

  const mapped = useMemo(() => (parsed ? toImportRows(parsed.rows, parsed.map, parsed.header) : []), [parsed]);
  const skipped = parsed ? parsed.rows.length - mapped.length : 0;
  const hasAmount = parsed ? parsed.map.amount != null || parsed.map.debit != null || parsed.map.credit != null : false;
  const ready = Boolean(parsed && accountId && parsed.map.date != null && hasAmount && mapped.length > 0);

  async function confirm() {
    if (!parsed) return;
    setBusy(true); setError(null);
    try {
      const r = await bank.import(accountId, parsed.filename, mapped);
      setResult(r);
      setParsed(null);
      setFileKey((k) => k + 1);
      onImported();
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  const setMap = (key: keyof ColumnMap, v: string) => {
    if (!parsed) return;
    const next = { ...parsed.map };
    if (v === '') delete next[key]; else next[key] = Number(v);
    setParsed({ ...parsed, map: next });
  };

  return (
    <Section title="Import a statement">
      <p className="muted">Export a CSV from the bank and choose it here. Lines that are already in the books are skipped by their transaction id, or by date, amount and description when the file has no id.</p>
      <div className="lp-form-grid">
        <Field label="Into account">
          <select className="lp-inp" value={accountId} onChange={(e) => setAccountId(e.target.value)} disabled={!canEdit}>
            <option value="">Choose an account</option>
            {rows.filter((a) => a.is_active || a.id === accountId).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </Field>
        <Field label="CSV file">
          <input key={fileKey} className="lp-inp" type="file" accept=".csv,text/csv" disabled={!canEdit || !accountId} onChange={(e) => void onFile(e)} />
        </Field>
      </div>
      <ErrorNote error={error} />
      {result && (
        <Note tone="good">
          Imported {result.rows_in} {result.rows_in === 1 ? 'line' : 'lines'}: {result.rows_new} new, {result.rows_dup} already in the books.
          {result.rows_new > 0 ? ' The new ones are waiting under For review.' : ''}
        </Note>
      )}
      {parsed && (
        <div className="lp-panel lp-panel--wash" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0 }}>Match up “{parsed.filename}”</h3>
          <p className="muted">Check which column holds what. If the bank splits money in and money out into two columns, set those and leave Amount blank.</p>
          <div className="lp-form-grid">
            {MAP_FIELDS.map((key) => (
              <Field key={key} label={COLUMN_LABELS[key]}>
                <select className="lp-inp" value={parsed.map[key] ?? ''} onChange={(e) => setMap(key, e.target.value)}>
                  <option value="">—</option>
                  {parsed.header.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                </select>
              </Field>
            ))}
          </div>
          {parsed.map.date == null && <Note tone="warn">Choose the date column.</Note>}
          {!hasAmount && <Note tone="warn">Choose an amount column, or money in and money out.</Note>}
          {mapped.length > 0 && (
            <>
              <p className="muted">
                {mapped.length} {mapped.length === 1 ? 'line reads' : 'lines read'} cleanly{skipped > 0 ? `; ${skipped} ${skipped === 1 ? 'has' : 'have'} no readable date or amount and will be skipped` : ''}. The first few:
              </p>
              <div className="lp-tbl-wrap">
                <table className="lp-tbl">
                  <thead><tr><th>Date</th><th>Description</th><th>Counterparty</th><th>Id</th><th className="num">Amount</th></tr></thead>
                  <tbody>
                    {mapped.slice(0, 5).map((r, i) => (
                      <tr key={i}>
                        <td>{day(r.posted_date)}</td>
                        <td>{r.description || '—'}</td>
                        <td>{r.counterparty ?? '—'}</td>
                        <td className="muted">{r.external_id ?? '—'}</td>
                        <td className="num"><Money cents={r.amount_cents} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          <div className="lp-row" style={{ marginTop: 12 }}>
            <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || busy || !ready} onClick={() => void confirm()}>
              {busy ? 'Importing…' : `Import ${mapped.length} ${mapped.length === 1 ? 'line' : 'lines'}`}
            </button>
            <button type="button" className="lp-btn lp-btn--quiet" disabled={busy} onClick={() => { setParsed(null); setFileKey((k) => k + 1); }}>Cancel</button>
          </div>
        </div>
      )}
    </Section>
  );
}

// ── transactions ─────────────────────────────────────────────────────────────

type Draft = { account_id: string; vendor_id: string; memo: string };

function suggestVendor(t: BankTransaction, vendors: Vendor[]): Vendor | null {
  const hay = `${t.description ?? ''} ${t.counterparty ?? ''}`.toLowerCase();
  if (!hay.trim()) return null;
  for (const v of vendors) {
    if (!v.is_active) continue;
    if ((v.match_text ?? []).some((m) => m && hay.includes(m.toLowerCase()))) return v;
  }
  return null;
}

function TransactionsSection({ account, accounts, byId, vendors, canEdit, onPosted }: {
  account: BankAccount;
  accounts: Account[];
  byId: Map<string, Account>;
  vendors: Vendor[];
  canEdit: boolean;
  onPosted: () => void;
}) {
  const [status, setStatus] = useState<BankTransaction['status']>('for_review');
  const txns = useLoad(() => bank.transactions(account.id, status), [account.id, status]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [panel, setPanel] = useState<{ id: string; kind: 'match' | 'exclude' } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  const list = txns.data ?? [];
  const categoryAccounts = useMemo(() => accounts.filter((a) => a.id !== account.account_id), [accounts, account.account_id]);

  const draftFor = (t: BankTransaction): Draft => {
    const d = drafts[t.id];
    if (d) return d;
    const v = suggestVendor(t, vendors);
    return { account_id: v?.default_account_id ?? '', vendor_id: v?.id ?? '', memo: '' };
  };
  const setDraft = (id: string, d: Draft) => setDrafts((all) => ({ ...all, [id]: d }));

  async function act(id: string, label: string, fn: () => Promise<unknown>) {
    setBusyId(id); setErr(null); setDone(null);
    try {
      await fn();
      setDone(label);
      setPanel(null);
      txns.reload();
      onPosted();
    } catch (e) { setErr(e); } finally { setBusyId(null); }
  }

  const exportCsv = () => {
    downloadCsv(`${account.name.replace(/\s+/g, '-')}-${status}.csv`, [
      ['Date', 'Description', 'Counterparty', 'Amount', 'Status', 'Posted to', 'Note'],
      ...list.map((t) => [t.posted_date, t.description ?? '', t.counterparty ?? '', (t.amount_cents / 100).toFixed(2), STATUS_WORDS[t.status], t.account_id ? byId.get(t.account_id)?.name ?? '' : '', t.note ?? '']),
    ]);
  };

  return (
    <Section
      title={`Transactions · ${account.name}`}
      right={(
        <div className="lp-row lp-no-print">
          <div className="lp-tabs">
            {(['for_review', 'posted', 'excluded'] as BankTransaction['status'][]).map((s) => (
              <button key={s} type="button" className={status === s ? 'on' : ''} onClick={() => setStatus(s)}>{STATUS_WORDS[s]}</button>
            ))}
          </div>
          <button type="button" className="lp-btn lp-btn--quiet" disabled={list.length === 0} onClick={exportCsv}>CSV</button>
        </div>
      )}
    >
      <ErrorNote error={txns.error} />
      <ErrorNote error={err} />
      {done && <Note tone="good">{done}</Note>}
      {txns.loading ? <Empty>Loading…</Empty> : list.length === 0 ? (
        <Empty>
          {status === 'for_review' ? 'Nothing waiting for review. Import a statement to add lines.' : status === 'posted' ? 'Nothing posted from this account yet.' : 'Nothing excluded.'}
        </Empty>
      ) : status === 'for_review' ? (
        <>
          <p className="muted">Pick the account each line belongs to and Add it. Match links a line to an entry that is already in the books, like an Apple payment or a distribution. Exclude sets a line aside with a reason.</p>
          <div className="lp-tbl-wrap">
            <table className="lp-tbl">
              <thead><tr><th>Date</th><th>Description</th><th className="num">Amount</th><th>Account</th><th>Vendor</th><th className="lp-no-print" /></tr></thead>
              <tbody>
                {list.map((t) => {
                  const d = draftFor(t);
                  const busy = busyId === t.id;
                  const open = panel?.id === t.id ? panel.kind : null;
                  return (
                    <ReviewRow
                      key={t.id}
                      t={t}
                      d={d}
                      busy={busy}
                      open={open}
                      canEdit={canEdit}
                      categoryAccounts={categoryAccounts}
                      vendors={vendors}
                      onDraft={(nd) => setDraft(t.id, nd)}
                      onOpen={(k) => setPanel(open === k ? null : { id: t.id, kind: k })}
                      onAdd={() => act(t.id, `Added ${dollars(Math.abs(t.amount_cents))} to ${byId.get(d.account_id)?.name ?? 'the account'}.`, () => bank.categorize(t.id, d.account_id, d.vendor_id || null, d.memo.trim() || null))}
                      onMatch={(entry) => act(t.id, `Matched to the entry from ${day(entry.entry_date)}.`, () => bank.match(t.id, entry.id))}
                      onExclude={(reason) => act(t.id, 'Excluded.', () => bank.exclude(t.id, reason))}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="lp-tbl-wrap">
          <table className="lp-tbl">
            <thead>
              <tr><th>Date</th><th>Description</th><th className="num">Amount</th><th>{status === 'posted' ? 'Posted to' : 'Reason'}</th>{status === 'posted' && <th className="lp-no-print" />}</tr>
            </thead>
            <tbody>
              {list.map((t) => (
                <tr key={t.id}>
                  <td>{day(t.posted_date)}</td>
                  <td>
                    <div>{t.description || '—'}</div>
                    {t.counterparty && t.counterparty !== t.description && <div className="muted">{t.counterparty}</div>}
                  </td>
                  <td className="num"><Money cents={t.amount_cents} /></td>
                  <td>
                    {status === 'posted'
                      ? (t.account_id ? (byId.get(t.account_id)?.name ?? '—') : t.entry_id ? 'Matched to an existing entry' : '—')
                      : (t.note || '—')}
                  </td>
                  {status === 'posted' && (
                    <td className="lp-no-print">
                      <ConfirmButton
                        label="Undo"
                        sentence="Undo this posting? Its entry is removed and the line goes back to For review."
                        yes="Yes, undo"
                        disabled={!canEdit || busyId === t.id}
                        onConfirm={() => act(t.id, 'Back under For review.', () => bank.unpost(t.id))}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

function ReviewRow({ t, d, busy, open, canEdit, categoryAccounts, vendors, onDraft, onOpen, onAdd, onMatch, onExclude }: {
  t: BankTransaction;
  d: Draft;
  busy: boolean;
  open: 'match' | 'exclude' | null;
  canEdit: boolean;
  categoryAccounts: Account[];
  vendors: Vendor[];
  onDraft: (d: Draft) => void;
  onOpen: (k: 'match' | 'exclude') => void;
  onAdd: () => void;
  onMatch: (e: JournalEntry) => void;
  onExclude: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <>
      <tr>
        <td>{day(t.posted_date)}</td>
        <td>
          <div>{t.description || '—'}</div>
          {t.counterparty && t.counterparty !== t.description && <div className="muted">{t.counterparty}</div>}
        </td>
        <td className="num"><Money cents={t.amount_cents} /></td>
        <td style={{ minWidth: 220 }}>
          <AccountSelect value={d.account_id} onChange={(id) => onDraft({ ...d, account_id: id })} accounts={categoryAccounts} disabled={!canEdit || busy} />
        </td>
        <td style={{ minWidth: 160 }}>
          <select
            className="lp-inp"
            value={d.vendor_id}
            disabled={!canEdit || busy}
            onChange={(e) => {
              const v = vendors.find((x) => x.id === e.target.value) ?? null;
              onDraft({ ...d, vendor_id: e.target.value, account_id: v?.default_account_id ?? d.account_id });
            }}
          >
            <option value="">No vendor</option>
            {vendors.filter((v) => v.is_active || v.id === d.vendor_id).map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </td>
        <td className="lp-no-print">
          <div className="lp-row">
            <button type="button" className="lp-btn lp-btn--primary lp-btn--small" disabled={!canEdit || busy || !d.account_id} onClick={onAdd}>{busy ? '…' : 'Add'}</button>
            <button type="button" className={`lp-btn lp-btn--small${open === 'match' ? '' : ' lp-btn--quiet'}`} disabled={!canEdit || busy} onClick={() => onOpen('match')}>Match</button>
            <button type="button" className={`lp-btn lp-btn--small${open === 'exclude' ? '' : ' lp-btn--quiet'}`} disabled={!canEdit || busy} onClick={() => onOpen('exclude')}>Exclude</button>
          </div>
        </td>
      </tr>
      {open === 'match' && (
        <tr className="quiet">
          <td colSpan={6}><MatchPanel t={t} busy={busy} canEdit={canEdit} onMatch={onMatch} /></td>
        </tr>
      )}
      {open === 'exclude' && (
        <tr className="quiet">
          <td colSpan={6}>
            <div className="lp-row">
              <input className="lp-inp" style={{ flex: 1, minWidth: 220 }} placeholder="Why this line stays out of the books (a transfer, a personal charge, a duplicate)" value={reason} onChange={(e) => setReason(e.target.value)} disabled={!canEdit || busy} />
              <button type="button" className="lp-btn lp-btn--danger lp-btn--small" disabled={!canEdit || busy || !reason.trim()} onClick={() => onExclude(reason.trim())}>Exclude this line</button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function MatchPanel({ t, busy, canEdit, onMatch }: { t: BankTransaction; busy: boolean; canEdit: boolean; onMatch: (e: JournalEntry) => void }) {
  const abs = Math.abs(t.amount_cents);
  const page = useLoad(() => journal.page({ from: addDays(t.posted_date, -7), to: addDays(t.posted_date, 7), limit: 200 }), [t.id]);
  const candidates = useMemo(
    () => (page.data?.entries ?? []).filter((e) => e.source !== 'bank' && !e.is_reversed && e.source !== 'reversal'
      && e.lines.some((l) => Number(l.debit_cents) === abs || Number(l.credit_cents) === abs)),
    [page.data, abs],
  );
  if (page.loading) return <p className="muted" style={{ margin: 0 }}>Looking for entries within a week of {day(t.posted_date)} for {dollars(abs)}…</p>;
  if (page.error) return <ErrorNote error={page.error} />;
  if (candidates.length === 0) return <p className="muted" style={{ margin: 0 }}>No entry within a week of this date for {dollars(abs)}. Add it with an account instead.</p>;
  return (
    <div className="lp-tbl-wrap">
      <table className="lp-tbl">
        <thead><tr><th>Entry date</th><th>Memo</th><th>Source</th><th>Lines</th><th className="lp-no-print" /></tr></thead>
        <tbody>
          {candidates.map((e) => (
            <tr key={e.id}>
              <td>{day(e.entry_date)}</td>
              <td>{e.memo || '—'}</td>
              <td>{SOURCE_LABELS[e.source] ?? e.source}</td>
              <td className="muted">{e.lines.map((l) => `${l.code ?? ''} ${l.name ?? ''}`.trim()).join(' · ')}</td>
              <td className="lp-no-print"><button type="button" className="lp-btn lp-btn--small lp-btn--primary" disabled={!canEdit || busy} onClick={() => onMatch(e)}>Use this entry</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── reconcile ────────────────────────────────────────────────────────────────

function ReconcileSection({ account, ledgerBalance, canEdit }: { account: BankAccount; ledgerBalance: number | null; canEdit: boolean }) {
  const history = useLoad(() => bank.reconciliations(account.id), [account.id]);
  const [date, setDate] = useState(today());
  const [balance, setBalance] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [result, setResult] = useState<BankReconciliation | null>(null);

  async function run() {
    if (balance == null) return;
    setBusy(true); setErr(null); setResult(null);
    try {
      const r = await bank.reconcile(account.id, date, balance, note.trim());
      setResult(r);
      history.reload();
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }

  const rows = history.data ?? [];
  return (
    <Section title={`Reconcile · ${account.name}`}>
      <p className="muted">
        Enter the closing balance from the statement. The ledger balance through that date is compared with it and the difference is kept on record.
        {ledgerBalance != null && <> Ledger balance today: <Money cents={ledgerBalance} />.</>}
      </p>
      <div className="lp-form-grid">
        <Field label="Statement date"><input className="lp-inp" type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={!canEdit} /></Field>
        <Field label="Statement balance"><MoneyInput cents={balance} onChange={setBalance} disabled={!canEdit} allowNegative /></Field>
        <Field label="Note"><input className="lp-inp" value={note} onChange={(e) => setNote(e.target.value)} disabled={!canEdit} placeholder="Optional" /></Field>
      </div>
      <div className="lp-row">
        <button type="button" className="lp-btn lp-btn--primary" disabled={!canEdit || busy || balance == null || !date} onClick={() => void run()}>{busy ? 'Comparing…' : 'Reconcile'}</button>
      </div>
      <ErrorNote error={err} />
      {result && (
        <Note tone={result.difference_cents === 0 ? 'good' : 'warn'}>
          Statement <Money cents={result.statement_balance_cents} /> · ledger <Money cents={result.ledger_balance_cents} /> · difference <Money cents={result.difference_cents} />.
          {result.difference_cents === 0 ? ' They agree.' : ' Look for lines still under For review, or entries dated after the statement.'}
        </Note>
      )}
      <ErrorNote error={history.error} />
      {history.loading ? <Empty>Loading…</Empty> : rows.length === 0 ? <Empty>No reconciliations yet for this account.</Empty> : (
        <div className="lp-tbl-wrap" style={{ marginTop: 12 }}>
          <table className="lp-tbl">
            <thead><tr><th>Statement date</th><th className="num">Statement</th><th className="num">Ledger</th><th className="num">Difference</th><th>Note</th><th>Done</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{day(r.statement_date)}</td>
                  <td className="num"><Money cents={r.statement_balance_cents} /></td>
                  <td className="num"><Money cents={r.ledger_balance_cents} /></td>
                  <td className="num">
                    <span className={`lp-tag ${Number(r.difference_cents) === 0 ? 'lp-tag--good' : 'lp-tag--warn'}`}><Money cents={r.difference_cents} /></span>
                  </td>
                  <td>{r.note || '—'}</td>
                  <td className="muted">{day(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
